/**
 * What: Normalizes the merchant sync-status payload into one canonical shape for the polling hook.
 * Transport / data-flow: Browser-side. Accepts either the per-store build projection (#2971) or the
 *   legacy Web-owned ingestion status and maps both onto the canonical `StoreSyncStatus` the hook
 *   already renders — so a `published` build reads as `completed` and a `failed`
 *   build reads as `failed` (existing error copy path).
 */
import { productLanguage, shopifyComponentCopy } from '@testfeed/product-language'
import {
  storeSyncAcceptedSchema,
  storeSyncBuildAcceptedSchema,
  storeSyncBuildStatusSchema,
  storeSyncStatusSchema,
  type StoreSyncAccepted,
  type StoreSyncBuildAccepted,
  type StoreSyncBuildStatus,
  type StoreSyncStatus as CanonicalStoreSyncStatus,
} from '@testfeed/shopify-contracts'
import { parseContractPayload } from './merchantErrors'

type BuildProgress = NonNullable<CanonicalStoreSyncStatus['buildProgress']>

const STORE_BUILD_WORKFLOW_DISPATCH_AMBIGUOUS = 'STORE_BUILD_WORKFLOW_DISPATCH_AMBIGUOUS'

function buildBaseStatus(
  status: CanonicalStoreSyncStatus['status'],
  syncActive: boolean,
  buildProgress: BuildProgress | null,
): CanonicalStoreSyncStatus {
  return {
    status,
    stage: firstNonCompletedKind(buildProgress) ?? 'build',
    coverage: null,
    activeUnits: syncActive ? 1 : 0,
    retryAt: null,
    publicationPending: false,
    errorMessage: null,
    syncActive,
    buildProgress,
  }
}

/** The first stage still in flight, settings-first; `null` once every stage has finished (done, failed or denied). */
function firstNonCompletedKind(buildProgress: BuildProgress | null): string | null {
  const stage = buildProgress?.stages.find(
    (candidate) =>
      candidate.state !== 'completed' && candidate.state !== 'failed' && candidate.state !== 'scope_denied',
  )
  return stage?.kind ?? null
}

/** Keeps an unresolved dispatch actionable without converting the persisted build into a terminal failure. */
function runningBuildErrorMessage(errorCode: string | null): string | null {
  return errorCode === STORE_BUILD_WORKFLOW_DISPATCH_AMBIGUOUS
    ? shopifyComponentCopy.storeIntelligenceStoreIntelligencePanel.syncDispatchUncertain051
    : null
}

/** Maps the per-store build projection onto the canonical sync-status the hook renders. */
export function adaptBuildSyncStatus(build: StoreSyncBuildStatus): CanonicalStoreSyncStatus {
  const latest = build.latest
  const withCoverage = (status: CanonicalStoreSyncStatus): CanonicalStoreSyncStatus => ({
    ...status,
    coverage: build.coverage ?? null,
  })
  if (!latest) return withCoverage(buildBaseStatus('idle', false, null))
  const buildProgress: BuildProgress = { stages: latest.stages, catalogReadyAt: latest.catalogReadyAt }
  if (latest.status === 'running') {
    return withCoverage({
      ...buildBaseStatus('running', true, buildProgress),
      errorMessage: runningBuildErrorMessage(latest.errorCode),
    })
  }
  // Publication can precede the remaining resource exports. Keep observing their
  // projected activity until every stage has settled, not just the build itself.
  if (latest.status === 'published')
    return withCoverage(buildBaseStatus('completed', firstNonCompletedKind(buildProgress) !== null, buildProgress))
  if (latest.status === 'failed') {
    return withCoverage({
      ...buildBaseStatus('failed', false, buildProgress),
      errorMessage: productLanguage.shopify.sync.failed,
    })
  }
  // superseded: a newer build replaced this one; nothing for the merchant to act on.
  return withCoverage(buildBaseStatus('idle', false, null))
}

/** Parses the merchant status payload, accepting either the build projection or the legacy shape. */
export function parseStoreSyncStatusResponse(value: unknown): CanonicalStoreSyncStatus {
  const build = storeSyncBuildStatusSchema.safeParse(value)
  if (build.success) return adaptBuildSyncStatus(build.data)
  return parseContractPayload('storeSyncStatusSchema', storeSyncStatusSchema, value)
}

/** Parses the sync-now acceptance, accepting either the build receipt or the legacy receipt. */
export function parseStoreSyncAcceptedResponse(value: unknown): StoreSyncAccepted | StoreSyncBuildAccepted {
  const build = storeSyncBuildAcceptedSchema.safeParse(value)
  if (build.success) return build.data
  return parseContractPayload('storeSyncAcceptedSchema', storeSyncAcceptedSchema, value)
}
